import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { getStoredGame, isGameCode, normalizeGameCode, saveStoredGame } from "../lib/storage";
import { Alert, AppShell, Button, Field } from "../components/ui";

export function JoinScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const code = normalizeGameCode(params.code ?? "");
  const validCode = isGameCode(code);
  const saved = useMemo(() => (validCode ? getStoredGame(code) : null), [code, validCode]);
  const [nickname, setNickname] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const join = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = nickname.trim();
    if (!trimmed) {
      setError("Enter a nickname to claim your seat.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.joinGame(code, trimmed);
      saveStoredGame({
        code: result.game.code,
        token: result.token,
        seat: result.seat,
        nickname: result.game.me.nickname,
        createdAt: result.game.createdAt
      });
      navigate(`/game/${result.game.code}`, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 404) setError("That game code is invalid or has expired.");
        else if (caught.status === 409)
          setError("Both seats in this game have already been claimed.");
        else setError(caught.message);
      } else setError("Could not join the game. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (params.code === undefined) {
    return (
      <AppShell>
        <main className="join-page">
          <section className="join-card">
            <p className="eyebrow">PRIVATE INVITE / GAME CODE</p>
            <h1>Find the board.</h1>
            <p>Enter the private 10-character code your rival sent you.</p>
            <form
              className="join-card__form"
              onSubmit={(event) => {
                event.preventDefault();
                const normalized = normalizeGameCode(manualCode);
                if (isGameCode(normalized)) navigate(`/join/${normalized}`);
                else setError("Game codes contain exactly 10 letters or numbers.");
              }}
            >
              <Field label="Game code" error={error ?? ""}>
                <input
                  value={manualCode}
                  onChange={(event) => {
                    setManualCode(normalizeGameCode(event.target.value));
                    setError(null);
                  }}
                  maxLength={10}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="AB12CD34EF"
                  data-testid="join-code"
                />
              </Field>
              <Button
                variant="primary"
                className="button--huge"
                type="submit"
                data-testid="join-go"
              >
                Continue →
              </Button>
            </form>
          </section>
        </main>
      </AppShell>
    );
  }

  if (!validCode) {
    return (
      <AppShell>
        <main className="page page--narrow error-page" data-testid="invalid-code">
          <p className="eyebrow">INVITE / INVALID</p>
          <h1>This code doesn’t fit.</h1>
          <p>A game code has exactly 10 letters or numbers. Check the invite and try again.</p>
          <Link className="button button--primary" to="/">
            Enter a code
          </Link>
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="join-page">
        <section className="join-card">
          <p className="eyebrow">PRIVATE INVITE / SEAT TWO</p>
          <h1>You’ve been called out.</h1>
          <div className="invite-code" aria-label={`Game code ${code}`}>
            <span>GAME</span>
            <strong>{code}</strong>
          </div>
          {saved ? (
            <Alert tone="warning">
              This device already has a private key for this game. You can resume that player
              instead of claiming another seat.
              <Link to={`/game/${code}`}> Resume saved game →</Link>
            </Alert>
          ) : null}
          <form className="join-card__form" onSubmit={join} data-testid="join-form">
            <Field
              label="Choose your nickname"
              hint="Your rival will see this name throughout the game."
            >
              <input
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                maxLength={24}
                autoComplete="nickname"
                autoFocus
                placeholder="e.g. Vowel Vandal"
                data-testid="join-nickname"
              />
            </Field>
            {error && <Alert tone="error">{error}</Alert>}
            <Button
              className="button--huge"
              variant="primary"
              type="submit"
              disabled={busy}
              data-testid="join-game"
            >
              {busy ? "Claiming seat…" : "Claim seat →"}
            </Button>
          </form>
          <p className="privacy-copy">
            No account. No public lobby. Your access stays private to this browser and your resume
            link.
          </p>
        </section>
      </main>
    </AppShell>
  );
}
